import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { notificationApi } from '../lib/api.js';
import { queryKeys } from '../lib/query-keys.js';
import type {
  SlackChannelInput,
  NotificationRuleInput,
  TestNotificationInput,
} from '../types/notification.js';

/**
 * @graph-stack ac-mens-hub
 * @graph-domain Engineering
 * @graph-business 通知チャンネル一覧を取得するフック。
 * @graph-connects notificationApi [calls] チャンネル API クライアント
 * @graph-connects queryKeys [calls] Query key factory
 */
export function useNotificationChannels() {
  return useQuery({
    queryKey: queryKeys.notifications.channels,
    queryFn: notificationApi.listChannels,
    staleTime: 60_000,
    retry: false,
  });
}

/**
 * @graph-stack ac-mens-hub
 * @graph-domain Engineering
 * @graph-business チャンネルを新規作成するミューテーション。成功時に一覧を invalidate する。
 * @graph-connects notificationApi [calls] チャンネル作成
 * @graph-connects queryKeys [calls] Query key factory
 */
export function useCreateNotificationChannel() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: SlackChannelInput) => notificationApi.createChannel(input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.notifications.channels });
    },
  });
}

/**
 * @graph-stack ac-mens-hub
 * @graph-domain Engineering
 * @graph-business チャンネルを更新するミューテーション。成功時に一覧を invalidate する。
 * @graph-connects notificationApi [calls] チャンネル更新
 * @graph-connects queryKeys [calls] Query key factory
 */
export function useUpdateNotificationChannel() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ channelId, input }: { channelId: string; input: SlackChannelInput }) =>
      notificationApi.updateChannel(channelId, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.notifications.channels });
    },
  });
}

/**
 * @graph-stack ac-mens-hub
 * @graph-domain Engineering
 * @graph-business チャンネルを削除するミューテーション。成功時に一覧を invalidate する。
 * @graph-connects notificationApi [calls] チャンネル削除
 * @graph-connects queryKeys [calls] Query key factory
 */
export function useDeleteNotificationChannel() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (channelId: string) => notificationApi.removeChannel(channelId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.notifications.channels });
    },
  });
}

/**
 * @graph-stack ac-mens-hub
 * @graph-domain Engineering
 * @graph-business 通知ルール一覧を取得するフック。
 * @graph-connects notificationApi [calls] ルール API クライアント
 * @graph-connects queryKeys [calls] Query key factory
 */
export function useNotificationRules() {
  return useQuery({
    queryKey: queryKeys.notifications.rules,
    queryFn: notificationApi.listRules,
    staleTime: 60_000,
    retry: false,
  });
}

/**
 * @graph-stack ac-mens-hub
 * @graph-domain Engineering
 * @graph-business ルールを作成・更新するミューテーション。成功時に一覧を invalidate する。
 * @graph-connects notificationApi [calls] ルール作成・更新
 * @graph-connects queryKeys [calls] Query key factory
 */
export function useSaveNotificationRule() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ isNew, input }: { isNew: boolean; input: NotificationRuleInput }) =>
      isNew
        ? notificationApi.createRule(input)
        : notificationApi.updateRule(input.notificationType, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.notifications.rules });
    },
  });
}

/**
 * @graph-stack ac-mens-hub
 * @graph-domain Engineering
 * @graph-business ルールを削除するミューテーション。成功時に一覧を invalidate する。
 * @graph-connects notificationApi [calls] ルール削除
 * @graph-connects queryKeys [calls] Query key factory
 */
export function useDeleteNotificationRule() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (notificationType: string) => notificationApi.removeRule(notificationType),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.notifications.rules });
    },
  });
}

/**
 * @graph-stack ac-mens-hub
 * @graph-domain Engineering
 * @graph-business テスト通知を送信するミューテーション。
 * @graph-connects notificationApi [calls] テスト通知送信
 */
export function useSendTestNotification() {
  return useMutation({
    mutationFn: (input: TestNotificationInput) => notificationApi.sendTest(input),
  });
}
